import { useState, useEffect, useMemo } from 'react';
import {
  collection, onSnapshot, doc, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { useItemParam } from '../hooks/useNewParam';
import { Button, Modal, ModalBanner, ConfirmModal, FormField, DeleteIconButton, EntityCombobox } from '../components/ui';
import { HeaderSearch, ChoiceButton, DateRangeButton, StatChips, IconPlusSm } from '../components/HeaderControls';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import { computeDateWindow, inWindow, type DateFilterKey } from '../utils/ledgerReports';
import type { InventoryItem, StockTransfer } from '../types';

function todayISO() { return new Date().toISOString().slice(0, 10); }
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
// createdAt is written via serverTimestamp() (a Firestore Timestamp once
// synced back) even though the app's own types declare it as `string` — a
// plain .localeCompare() on it throws and crashes the page the moment two
// rows share the same date and the sort's tie-breaker actually runs. Reads
// either shape (Timestamp instance, plain {seconds}, or an ISO string)
// into sortable millis instead. Mirrors Dashboard.tsx's own toMillis.
function toMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') return new Date(v).getTime();
  const obj = v as { toMillis?: () => number; seconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') return obj.seconds * 1000;
  return 0;
}
const fmtDate = (s?: string) =>
  s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const availCarat = (i: InventoryItem) => round2((i.carat ?? 0) - (i.memoCommittedCarat ?? 0));
const UNSET = 'Unassigned';

const PAGE_SIZE = 50;

// ── Pagination (same pattern as Purchase/Sales/Inventory/Entity/Memos/AP/AR/SalesReturns) ──
function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);
  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3)         pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> transfer{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

// ── Move modal ──────────────────────────────────────────────────────────
function MoveModal({ items, knownLocations, onClose, initialItemId }: {
  items: InventoryItem[]; knownLocations: string[]; onClose: () => void; initialItemId?: string;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [itemId, setItemId] = useState(initialItemId ?? '');
  const [date, setDate] = useState(todayISO());
  const [carat, setCarat] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const item = items.find(i => i.id === itemId) ?? null;
  const avail = item ? availCarat(item) : 0;
  const cut = parseFloat(carat) || 0;
  const isWhole = !!item && cut >= avail - 1e-6;

  async function save() {
    if (!item) return setError('Pick an item.');
    if (!toLocation.trim()) return setError('Enter the destination location.');
    if ((toLocation.trim()) === (item.location || UNSET)) return setError('That item is already in that location.');
    if (cut <= 0) return setError('Enter a carat to move.');
    if (cut > avail + 1e-6) return setError(`Only ${avail.toFixed(2)} ct is available to move.`);

    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }

    const prefix = (settings.stockTransferPrefix || 'ST').toUpperCase();
    const trRef = doc(collection(db, 'stockTransfers'));
    const itemRef = doc(db, 'inventory', item.id);
    const counterRef = doc(db, 'counters', business.id);
    const sliceRef = doc(collection(db, 'inventory'));
    const toLoc = toLocation.trim();

    try {
      await runTransaction(db, async tx => {
        const [cs, is] = await Promise.all([tx.get(counterRef), tx.get(itemRef)]);
        if (!is.exists()) throw new Error('That item no longer exists.');
        const cur = { id: is.id, ...is.data() } as InventoryItem;
        if (cur.status !== 'in-stock') throw new Error('That item is no longer in stock.');
        const availFresh = round2((cur.carat ?? 0) - (cur.memoCommittedCarat ?? 0));
        if (cut > availFresh + 1e-6) throw new Error(`Only ${availFresh.toFixed(2)} ct is available to move.`);

        const whole = cut >= availFresh - 1e-6;
        const fromLoc = cur.location || '';
        const n = (cs.exists() ? (cs.data().stockTransfer ?? 0) : 0) + 1;
        const trNo = `${prefix}-${new Date().getFullYear()}-${String(n).padStart(4, '0')}`;

        tx.set(counterRef, { stockTransfer: n }, { merge: true });

        if (whole) {
          tx.update(itemRef, { location: toLoc, updatedAt: serverTimestamp() });
        } else {
          const ratio = cut / (cur.carat ?? cut);
          const sliceCost = round2((cur.costUSD ?? 0) * ratio);
          const sliceCosting = round2((cur.costingUSD ?? 0) * ratio);
          tx.set(sliceRef, stripUndef({
            businessId: business.id,
            purchaseId: cur.purchaseId, purchaseNo: cur.purchaseNo, lineItemId: cur.lineItemId,
            type: cur.type, status: 'in-stock', provenance: cur.provenance,
            shape: cur.shape, carat: round2(cut), color: cur.color, clarity: cur.clarity,
            size: cur.size, subSize: cur.subSize, sku: cur.sku, skuId: cur.skuId,
            code: cur.code || undefined,
            costUSD: sliceCost, costingUSD: sliceCosting,
            location: toLoc,
            splitFromId: cur.splitFromId ?? cur.id,
            createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
          }));
          tx.update(itemRef, {
            carat: round2((cur.carat ?? 0) - cut),
            costUSD: round2((cur.costUSD ?? 0) - sliceCost),
            costingUSD: round2((cur.costingUSD ?? 0) - sliceCosting),
            updatedAt: serverTimestamp(),
          });
        }

        tx.set(trRef, stripUndef({
          businessId: business.id, transferNo: trNo, date,
          inventoryItemId: cur.id, sliceId: whole ? undefined : sliceRef.id,
          code: cur.code || undefined, shape: cur.shape, carat: round2(cut),
          fromLocation: fromLoc, toLocation: toLoc, wholeItem: whole,
          notes: notes.trim() || undefined,
          createdAt: new Date().toISOString(), createdBy: user!.id,
        }));
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not move the stock.');
    }
    setSaving(false);
  }

  const selectedItem = items.find(i => i.id === itemId);

  return (
    <Modal open onClose={onClose} hideHead
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>Move</Button>
      </>}>
      <ModalBanner title={selectedItem?.code || 'Move Stock'} eyebrow="Move Stock" onClose={onClose} />

      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

      <FormField label="Item" required>
        {/* Type a Code, shape or grade; Up / Down + Enter to pick (same picker as Company). */}
        <EntityCombobox
          entities={items.map(i => ({
            id: i.id,
            companyName: `${i.code ? `${i.code} · ` : ''}${i.shape} ${availCarat(i).toFixed(2)}ct ${i.color} ${i.clarity} · ${i.location || UNSET}`,
          }))}
          value={itemId}
          onChange={id => { setItemId(id); const it = items.find(i => i.id === id); setCarat(it ? String(availCarat(it)) : ''); }}
          placeholder="Type a Code, shape or grade…"
        />
      </FormField>

      <div className="form-row-2">
        <FormField label="From">
          <input value={item ? (item.location || UNSET) : ''} disabled readOnly />
        </FormField>
        <FormField label="To location" required>
          <input list="stock-locations" value={toLocation} onChange={e => setToLocation(e.target.value)} placeholder="e.g. Vault B" />
          <datalist id="stock-locations">
            {knownLocations.map(l => <option key={l} value={l} />)}
          </datalist>
        </FormField>
      </div>
      <div className="form-row-2">
        <FormField label="Carat to move" required hint={item ? `${avail.toFixed(2)} ct available${isWhole ? ' — moves the whole lot' : ' — splits off a slice'}` : undefined}>
          <input type="number" step="0.01" min="0" className="no-spinner" value={carat}
            onChange={e => setCarat(e.target.value)} placeholder="0.00" />
        </FormField>
        <FormField label="Date" required>
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
        </FormField>
      </div>
      <FormField label="Notes (optional)">
        <input value={notes} onChange={e => setNotes(e.target.value)} />
      </FormField>

      {item && cut > 0 && (
        <div style={{ marginTop: 6, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)', fontSize: 13 }}>
          Moving <strong>{round2(cut).toFixed(2)} ct</strong> ({money(round2((item.costUSD ?? 0) * (cut / (item.carat || cut))))})
          {' from '}<strong>{item.location || UNSET}</strong>{' to '}<strong>{toLocation.trim() || '…'}</strong>.
          {!isWhole && ' The rest of the lot stays where it is.'}
        </div>
      )}
    </Modal>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
export default function StockTransferPage() {
  const { business } = useAuth();
  const settings = useSettings();

  const [items, setItems] = useState<InventoryItem[]>([]);
  const [transfers, setTransfers] = useState<StockTransfer[]>([]);
  const [loading, setLoading] = useState(true);

  const [dateKey, setDateKey] = useState<DateFilterKey>('this-month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [locFilter, setLocFilter] = useState('all');
  const [searchQ, setSearchQ] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  // Opened from the Inventory popup (?item=…): start with that stone picked.
  const [preItem, setPreItem] = useState<string | undefined>();
  useItemParam(id => { setPreItem(id); setShowAdd(true); });
  const [confirm, setConfirm] = useState<StockTransfer | null>(null);
  const [busy, setBusy] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const u1 = onSnapshot(collection(db, 'inventory'),
      snap => setItems(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem)).filter(i => i.status === 'in-stock')),
      () => {});
    const u2 = onSnapshot(collection(db, 'stockTransfers'),
      snap => { setTransfers(snap.docs.map(d => ({ id: d.id, ...d.data() } as StockTransfer))); setLoading(false); },
      () => setLoading(false));
    return () => { u1(); u2(); };
  }, [business]);

  const knownLocations = useMemo(() => {
    const s = new Set<string>((settings.locations ?? []).filter(Boolean));
    items.forEach(i => { if (i.location) s.add(i.location); });
    transfers.forEach(t => { if (t.toLocation) s.add(t.toLocation); if (t.fromLocation) s.add(t.fromLocation); });
    return [...s].sort();
  }, [settings.locations, items, transfers]);

  const window = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);
  const rows = useMemo(() => {
    if (!window) return [];
    return transfers.filter(t => inWindow(t.date, window))
      .filter(t => locFilter === 'all' || t.toLocation === locFilter || t.fromLocation === locFilter)
      .filter(t => {
        const q = searchQ.trim().toLowerCase();
        return !q || [t.transferNo, t.code, t.fromLocation, t.toLocation].some(v => (v ?? '').toLowerCase().includes(q));
      })
      .sort((a, b) => b.date.localeCompare(a.date) || toMillis(b.createdAt) - toMillis(a.createdAt));
  }, [transfers, window, locFilter, searchQ]);

  useEffect(() => { setCurrentPage(1); }, [dateKey, customFrom, customTo, locFilter, searchQ]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const byLocation = useMemo(() => {
    const m = new Map<string, { carat: number; count: number }>();
    items.forEach(i => {
      const k = i.location || UNSET;
      const cur = m.get(k) ?? { carat: 0, count: 0 };
      cur.carat = round2(cur.carat + availCarat(i));
      cur.count += 1;
      m.set(k, cur);
    });
    return [...m.entries()].sort((a, b) => b[1].carat - a[1].carat);
  }, [items]);

  async function doDelete(t: StockTransfer) {
    setBusy(true);
    const db = getClientDb();
    if (!db || !business) { setBusy(false); return; }
    try {
      const itemRef = doc(db, 'inventory', t.inventoryItemId);
      await runTransaction(db, async tx => {
        if (t.wholeItem) {
          const is = await tx.get(itemRef);
          if (is.exists()) tx.update(itemRef, stripUndef({ location: t.fromLocation || null, updatedAt: serverTimestamp() }));
        } else if (t.sliceId) {
          const [is, ss] = await Promise.all([tx.get(itemRef), tx.get(doc(db, 'inventory', t.sliceId))]);
          // Fold the slice back only if it's untouched (still in-stock, same carat).
          if (ss.exists() && is.exists()) {
            const slice = ss.data() as InventoryItem;
            if (slice.status === 'in-stock' && Math.abs((slice.carat ?? 0) - (t.carat ?? 0)) < 1e-6) {
              const src = is.data() as InventoryItem;
              tx.update(itemRef, {
                carat: round2((src.carat ?? 0) + (slice.carat ?? 0)),
                costUSD: round2((src.costUSD ?? 0) + (slice.costUSD ?? 0)),
                costingUSD: round2((src.costingUSD ?? 0) + (slice.costingUSD ?? 0)),
                updatedAt: serverTimestamp(),
              });
              tx.delete(doc(db, 'inventory', t.sliceId));
            }
          }
        }
        tx.delete(doc(db, 'stockTransfers', t.id));
      });
    } finally { setBusy(false); setConfirm(null); }
  }

  return (
    <AppLayout
      title="Stock Transfer"
      subtitle="Move stock between physical locations — a records-only move, no effect on stock value or Profit."
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Ref, code, location…" label="Search transfers" />
        <DateRangeButton
          value={dateKey} onChange={setDateKey}
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
        />
        {knownLocations.length > 0 && (
          <ChoiceButton<string>
            label="Location" value={locFilter} onChange={setLocFilter} defaultKey="all"
            options={[{ key: 'all', label: 'All' }, ...knownLocations.map(l => ({ key: l, label: l }))]}
          />
        )}
        {(searchQ || dateKey !== 'this-month' || locFilter !== 'all') && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); setDateKey('this-month'); setLocFilter('all'); }}>Clear</button>
        )}
      </>}
      headerActions={
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => setShowAdd(true)} disabled={!business}
          style={{ height: 34, boxSizing: 'border-box' }}>Move Stock</Button>
      }
    >
      {byLocation.length > 0 && (
        <StatChips items={byLocation.slice(0, 6).map(([loc, v]) => ({
          label: loc, value: `${v.carat.toFixed(2)} ct · ${v.count} item${v.count === 1 ? '' : 's'}`,
        }))} />
      )}

      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 200, borderRadius: 8 }} /></div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No transfers here</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Use “Move Stock” above to relocate a lot.</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 120 }}>Ref</th>
                  <th>Item</th>
                  <th style={{ textAlign: 'right', width: 80 }}>Carat</th>
                  <th>From</th>
                  <th>To</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map(t => (
                  <tr key={t.id}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{fmtDate(t.date)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{t.transferNo}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {t.code ? <span style={{ fontFamily: 'var(--font-num)' }}>{t.code}</span> : null}{t.code ? ' · ' : ''}{t.shape}
                      {!t.wholeItem && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-3)' }}>(slice)</span>}
                    </td>
                    <td style={{ fontSize: 12, textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--text-2)' }}>{(t.carat ?? 0).toFixed(2)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-3)' }}>{t.fromLocation || UNSET}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-1)', fontWeight: 600 }}>{t.toLocation}</td>
                    <td className="col-actions">
                      <DeleteIconButton onClick={() => setConfirm(t)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <Pagination
          current={currentPage} total={totalPages}
          totalItems={rows.length} pageSize={PAGE_SIZE}
          onChange={setCurrentPage}
        />
      )}

      {showAdd && <MoveModal items={items} knownLocations={knownLocations} initialItemId={preItem} onClose={() => { setShowAdd(false); setPreItem(undefined); }} />}
      {confirm && (
        <ConfirmModal
          open
          title="Delete this transfer?"
          message={`${confirm.transferNo} will be removed and the stock moved back to ${confirm.fromLocation || UNSET}${confirm.wholeItem ? '' : ' (the slice is folded back into its source if untouched)'}.`}
          confirmLabel="Delete"
          loading={busy}
          onConfirm={() => doDelete(confirm)}
          onClose={() => setConfirm(null)}
        />
      )}
    </AppLayout>
  );
}
